/**
 * EntitlementsProvider - Single Source of Truth for Frontend Feature Gating
 * =========================================================================
 * 
 * This context fetches and caches entitlements from GET /api/me/entitlements.
 * All feature gating in the frontend MUST use this provider instead of
 * hardcoding tier logic or checking user.is_elite directly.
 * 
 * Usage:
 *   import { useEntitlements } from '../context/EntitlementsContext';
 *   
 *   function MyComponent() {
 *     const { hasFeature, getLimit, loading } = useEntitlements();
 *     
 *     if (!hasFeature('can_video_chat')) {
 *       return <UpgradePrompt />;
 *     }
 *     
 *     const loungeMinutes = getLimit('lounge_minutes_per_day');
 *     ...
 *   }
 */
import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';

// Match apiClient behavior: use env if set, otherwise fall back to same-origin.
const API_URL = window.location.origin;

// Default entitlements for unauthenticated/loading state
const DEFAULT_ENTITLEMENTS = {
  subscription: {
    tier: 'free',
    is_active: false,
    in_grace_period: false,
    scheduled_to_cancel: false,
    expires_at: null,
    source: null,
  },
  features: {
    can_lounge: true,
    can_speed_dating: true,
    can_video_chat: false,
    can_translate: true,
    can_tip: true,
    can_send_voice: false,
    can_speak_in_rooms: false,
    can_see_who_liked: false,
    can_ai_wingman: false,
    has_priority_matching: false,
    has_verified_badge: false,
    has_inner_circle_badge: false,
    has_elite_badge: false,
    has_after_hours_access: false,
  },
  limits: {
    lounge_minutes_per_day: 20,
    speed_dating_minutes_per_month: 30,
    profile_boosts_per_month: 0,
    daily_likes: -1,
    daily_messages: -1,
  },
  requirements: {
    identity_verification_required_for_voice: true,
    identity_verification_required_for_rooms: true,
    identity_verification_required_for_speed_dating: true,
    identity_verification_required_for_ticketed_events: true,
    identity_verification_required_for_tipping: false,
  },
  thresholds: {
    video_unlock_min_messages_each: 3,
    ticketed_event_minutes: 60,
  },
  verification: {
    identity_verified: false,
    age_verified: false,
    email_verified: false,
  },
  effective_tier: 'free',
  is_premium: false,
  version: 'v1.0.0',
};

// Create the context
const EntitlementsContext = createContext(null);

/**
 * EntitlementsProvider component
 * Wrap your app (inside AuthContext) with this provider.
 */
export function EntitlementsProvider({ children, token }) {
  const [entitlements, setEntitlements] = useState(DEFAULT_ENTITLEMENTS);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastFetchedAt, setLastFetchedAt] = useState(null);
  
  // Track if we've fetched for this token
  const lastTokenRef = useRef(null);
  
  /**
   * Fetch entitlements from the server
   */
  const fetchEntitlements = useCallback(async (authToken) => {
    if (!authToken) {
      setEntitlements(DEFAULT_ENTITLEMENTS);
      setError(null);
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_URL}/api/me/entitlements`, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch entitlements: ${response.status}`);
      }
      
      const data = await response.json();
      setEntitlements(data);
      setLastFetchedAt(new Date().toISOString());
      
      // Dev-only logging
      if (process.env.NODE_ENV !== 'production') {
        console.log('[Entitlements] Fetched:', {
          tier: data.effective_tier,
          features: Object.keys(data.features).filter(k => data.features[k]).length + ' enabled',
        });
      }
    } catch (err) {
      console.error('[Entitlements] Fetch error:', err);
      setError(err.message);
      // Keep previous entitlements on error (graceful degradation)
    } finally {
      setLoading(false);
    }
  }, []);
  
  /**
   * Refresh entitlements - call after subscription changes, verification, etc.
   */
  const refreshEntitlements = useCallback(() => {
    if (token) {
      fetchEntitlements(token);
    }
  }, [token, fetchEntitlements]);
  
  // Fetch on token change
  useEffect(() => {
    if (token && token !== lastTokenRef.current) {
      lastTokenRef.current = token;
      fetchEntitlements(token);
    } else if (!token) {
      lastTokenRef.current = null;
      setEntitlements(DEFAULT_ENTITLEMENTS);
    }
  }, [token, fetchEntitlements]);
  
  // =========================================================================
  // HELPER FUNCTIONS - Use these instead of checking entitlements directly
  // =========================================================================
  
  /**
   * Check if user has a feature enabled
   * @param {string} featureKey - e.g., 'can_video_chat', 'can_send_voice'
   * @returns {boolean}
   */
  const hasFeature = useCallback((featureKey) => {
    return entitlements.features?.[featureKey] ?? false;
  }, [entitlements]);
  
  /**
   * Get a numeric limit
   * @param {string} limitKey - e.g., 'lounge_minutes_per_day', 'speed_dating_minutes_per_month'
   * @returns {number}
   */
  const getLimit = useCallback((limitKey) => {
    return entitlements.limits?.[limitKey] ?? 0;
  }, [entitlements]);
  
  /**
   * Get a threshold value
   * @param {string} thresholdKey - e.g., 'video_unlock_min_messages_each'
   * @returns {number}
   */
  const getThreshold = useCallback((thresholdKey) => {
    return entitlements.thresholds?.[thresholdKey] ?? 0;
  }, [entitlements]);
  
  /**
   * Check if a feature requires verification
   * @param {string} requirementKey - e.g., 'identity_verification_required_for_voice'
   * @returns {boolean}
   */
  const requires = useCallback((requirementKey) => {
    return entitlements.requirements?.[requirementKey] ?? true;
  }, [entitlements]);
  
  /**
   * Check verification status
   * @param {string} verificationKey - e.g., 'identity_verified', 'age_verified'
   * @returns {boolean}
   */
  const isVerified = useCallback((verificationKey) => {
    return entitlements.verification?.[verificationKey] ?? false;
  }, [entitlements]);
  
  /**
   * Get the effective subscription tier
   * @returns {string} - 'free', 'inner_circle', 'elite', 'elite_plus', 'admin'
   */
  const getTier = useCallback(() => {
    return entitlements.effective_tier ?? 'free';
  }, [entitlements]);
  
  /**
   * Check if user has any premium subscription
   * @returns {boolean}
   */
  const isPremium = useCallback(() => {
    return entitlements.is_premium ?? false;
  }, [entitlements]);
  
  /**
   * Check if subscription is active
   * @returns {boolean}
   */
  const isSubscriptionActive = useCallback(() => {
    return entitlements.subscription?.is_active ?? false;
  }, [entitlements]);
  
  /**
   * Check if user is in grace period (payment failed)
   * @returns {boolean}
   */
  const isInGracePeriod = useCallback(() => {
    return entitlements.subscription?.in_grace_period ?? false;
  }, [entitlements]);
  
  // Context value
  const value = {
    // Raw entitlements data
    entitlements,
    
    // State
    loading,
    error,
    lastFetchedAt,
    
    // Actions
    refreshEntitlements,
    
    // Helper functions
    hasFeature,
    getLimit,
    getThreshold,
    requires,
    isVerified,
    getTier,
    isPremium,
    isSubscriptionActive,
    isInGracePeriod,
  };
  
  return (
    <EntitlementsContext.Provider value={value}>
      {children}
    </EntitlementsContext.Provider>
  );
}

/**
 * Hook to access entitlements context
 * @throws {Error} if used outside EntitlementsProvider
 */
export function useEntitlements() {
  const context = useContext(EntitlementsContext);
  if (!context) {
    throw new Error('useEntitlements must be used within EntitlementsProvider');
  }
  return context;
}

/**
 * Hook for components that can gracefully handle missing provider
 * Returns default values if provider is not available
 */
export function useEntitlementsSafe() {
  const context = useContext(EntitlementsContext);
  if (!context) {
    // Return safe defaults
    return {
      entitlements: DEFAULT_ENTITLEMENTS,
      loading: false,
      error: null,
      lastFetchedAt: null,
      refreshEntitlements: () => {},
      hasFeature: () => false,
      getLimit: (key) => DEFAULT_ENTITLEMENTS.limits[key] ?? 0,
      getThreshold: (key) => DEFAULT_ENTITLEMENTS.thresholds[key] ?? 0,
      requires: () => true,
      isVerified: () => false,
      getTier: () => 'free',
      isPremium: () => false,
      isSubscriptionActive: () => false,
      isInGracePeriod: () => false,
    };
  }
  return context;
}

// Export default entitlements for testing
export { DEFAULT_ENTITLEMENTS };
